import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Alert,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import equipmentModelsServices from '../services/equipmentModelsServices';
import equipmentMakesServices from '../services/equipmentMakesServices';
import equipmentTypesServices from '../services/equipmentTypesServices';
import proposalsServices from '../services/proposalsServices';
import proposalSystemsServices from '../services/proposalSystemsServices';
import propertiesServices from '../services/propertiesServices';

const ROWS_PER_PAGE = 25;

const formatPrice = (price) => {
  if (!price) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price);
};

const SORT_OPTIONS = [
  { value: 'name', label: 'Model' },
  { value: 'type', label: 'Type' },
  { value: 'make', label: 'Manufacturer' },
  { value: 'price', label: 'Price' },
  { value: 'suppliers', label: 'Suppliers' },
];

const EquipmentCatalog = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [makes, setMakes] = useState([]);
  const [types, setTypes] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE);
  const [filterMakeId, setFilterMakeId] = useState('');
  const [filterTypeId, setFilterTypeId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const [selectedItem, setSelectedItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [publicId, setPublicId] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addResult, setAddResult] = useState(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [propertySearch, setPropertySearch] = useState('');
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);

  const searchTimerRef = useRef(null);
  const propertySearchTimerRef = useRef(null);

  const loadMakes = async () => {
    try {
      const response = await equipmentMakesServices.getAll();
      setMakes(response.data.filter((m) => !m.deletedAt));
    } catch (error) {
      console.error(error);
    }
  };

  const loadTypes = async () => {
    try {
      const response = await equipmentTypesServices.getAll();
      setTypes(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await equipmentModelsServices.getAll({
        makeId: filterMakeId || null,
        typeId: filterTypeId || null,
        query: searchQuery || null,
        skip: page * rowsPerPage,
        take: rowsPerPage,
        sortBy,
        sortOrder: sortOrder.toUpperCase(),
      });
      setItems(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load equipment catalog');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filterMakeId, filterTypeId, searchQuery, page, rowsPerPage, sortBy, sortOrder]);

  useEffect(() => { loadMakes(); loadTypes(); }, []);
  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (propertySearchTimerRef.current) clearTimeout(propertySearchTimerRef.current);
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => { setSearchQuery(value); setPage(0); }, 500);
  };

  const handleClearSearch = () => { setSearchInput(''); setSearchQuery(''); setPage(0); };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const handleRowClick = (item) => {
    setSelectedItem(item);
    setDetailOpen(true);
    setPublicId('');
    setAddResult(null);
    setShowCreateForm(false);
    setSelectedProperty(null);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedItem(null);
    setAddResult(null);
    setShowCreateForm(false);
  };

  const handlePropertySearch = (value) => {
    setPropertySearch(value);
    if (propertySearchTimerRef.current) clearTimeout(propertySearchTimerRef.current);
    if (!value || value.length < 2) { setPropertyOptions([]); return; }
    propertySearchTimerRef.current = setTimeout(async () => {
      setPropertyLoading(true);
      try {
        const response = await propertiesServices.searchRequest(0, 20, value, false);
        setPropertyOptions(response.data?.data || response.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setPropertyLoading(false);
      }
    }, 400);
  };

  const addEquipmentToProposal = async (proposalId, proposalSystems) => {
    const item = selectedItem;
    let systemId;

    if (proposalSystems && proposalSystems.length > 0) {
      systemId = proposalSystems[0].id;
    } else {
      const sysResp = await proposalSystemsServices.createSystem(proposalId, {
        name: 'System one',
        installationPrice: 0,
      });
      systemId = sysResp.data.id;
    }

    const equipmentName = `${item.type?.name || ''} ${item.make?.name || ''} ${item.name}`.trim();
    await proposalSystemsServices.addEquipment(proposalId, systemId, {
      modelNumber: item.name,
      equipmentName,
      equipmentType: item.type?.name || '',
      manufacturerName: item.make?.name || '',
      priceAtCreation: parseFloat(item.price) || 0,
      description: item.description || '',
      imageUrl: item.resources?.[0]?.url || null,
    });

    return systemId;
  };

  const handleAddToContract = async () => {
    if (!publicId.trim()) {
      toast.warning('Please enter a contract public ID');
      return;
    }

    if (publicId.trim() === 'NEW') {
      setAddResult(null);
      setShowCreateForm(true);
      return;
    }

    setAddLoading(true);
    setAddResult(null);
    setShowCreateForm(false);

    try {
      const response = await proposalsServices.getProposalById(publicId.trim());
      const proposal = response.data;

      if (!proposal || !proposal.id) {
        setAddResult({ type: 'not_found' });
        setShowCreateForm(true);
        setAddLoading(false);
        return;
      }

      await addEquipmentToProposal(proposal.id, proposal.proposalSystems);
      setAddResult({ type: 'success', publicId: proposal.publicId || publicId.trim() });
      toast.success(`Equipment added to ${proposal.publicId || publicId.trim()}`);
    } catch (error) {
      if (error.response?.status === 404) {
        setAddResult({ type: 'not_found' });
        setShowCreateForm(true);
      } else {
        toast.error('Failed to add equipment');
        setAddResult({ type: 'error', message: error.response?.data?.message || 'Unknown error' });
      }
    } finally {
      setAddLoading(false);
    }
  };

  const handleCreateContractAndAdd = async () => {
    if (!selectedProperty) {
      toast.warning('Please select a property');
      return;
    }

    setCreateLoading(true);
    try {
      const proposalResp = await proposalsServices.create({
        status: 'Active',
        proposalType: 'Installation',
        property: { id: selectedProperty.id },
        emailTo: [''],
      });

      const newProposal = proposalResp.data;
      await addEquipmentToProposal(newProposal.id, []);
      setAddResult({ type: 'created', publicId: newProposal.publicId });
      setShowCreateForm(false);
      toast.success(`Contract ${newProposal.publicId} created and equipment added`);
    } catch (error) {
      toast.error('Failed to create contract');
      console.error(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const columns = [
    { id: 'name', label: 'Model', width: '22%' },
    { id: 'type', label: 'Type', width: '12%' },
    { id: 'make', label: 'Manufacturer', width: '12%' },
    { id: 'price', label: 'Price', width: '10%', align: 'right' },
    { id: 'suppliers', label: 'Suppliers', width: '14%' },
    { id: 'description', label: 'Description', width: '30%' },
  ];

  const renderFilters = () => (
    <Box sx={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: isMobile ? 'stretch' : 'flex-end',
      mb: 2,
      gap: isMobile ? 1.5 : 2,
    }}>
      <TextField
        size="small"
        placeholder="Search equipment..."
        value={searchInput}
        onChange={handleSearchChange}
        fullWidth={isMobile}
        sx={{ ...(!isMobile && { minWidth: 220 }) }}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
          endAdornment: searchInput && (
            <IconButton size="small" onClick={handleClearSearch}>
              <ClearIcon fontSize="small" />
            </IconButton>
          ),
        }}
      />
      <Stack direction="row" spacing={1} sx={{ width: isMobile ? '100%' : 'auto' }}>
        <FormControl sx={{ minWidth: isMobile ? 0 : 180, flex: isMobile ? 1 : 'none' }} size="small">
          <InputLabel>Type</InputLabel>
          <Select
            value={filterTypeId}
            label="Type"
            onChange={(e) => { setFilterTypeId(e.target.value); setPage(0); }}
          >
            <MenuItem value=""><em>All</em></MenuItem>
            {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: isMobile ? 0 : 180, flex: isMobile ? 1 : 'none' }} size="small">
          <InputLabel>Manufacturer</InputLabel>
          <Select
            value={filterMakeId}
            label="Manufacturer"
            onChange={(e) => { setFilterMakeId(e.target.value); setPage(0); }}
          >
            <MenuItem value=""><em>All</em></MenuItem>
            {makes.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {isMobile && (
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              label="Sort by"
              onChange={(e) => { setSortBy(e.target.value); setPage(0); }}
              startAdornment={<SortIcon sx={{ mr: 0.5, color: 'action.active', fontSize: 18 }} />}
            >
              {SORT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <IconButton
            size="small"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.75 }}
          >
            {sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </IconButton>
        </Stack>
      )}
    </Box>
  );

  const renderMobileCards = () => {
    if (loading) {
      return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={30} /></Box>;
    }
    if (!items.length) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No Data</Typography>
        </Box>
      );
    }
    return (
      <Stack spacing={1}>
        {items.map((item) => (
          <Card key={item.id} variant="outlined">
            <CardActionArea onClick={() => handleRowClick(item)}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, mr: 1 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', whiteSpace: 'nowrap' }}>
                    {formatPrice(item.price)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {item.type?.name && <Chip label={item.type.name} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                  {item.make?.name && <Chip label={item.make.name} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                </Stack>
                {item.description && (
                  <Typography variant="caption" color="text.secondary" sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mt: 0.5,
                    lineHeight: 1.3,
                  }}>
                    {item.description}
                  </Typography>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    );
  };

  const renderDesktopTable = () => (
    <Paper>
      <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  width={col.width}
                  align={col.align || 'left'}
                  sortDirection={sortBy === col.id ? sortOrder : false}
                >
                  <TableSortLabel
                    active={sortBy === col.id}
                    direction={sortBy === col.id ? sortOrder : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : items.length ? (
              items.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleRowClick(item)}
                >
                  <TableCell><strong>{item.name}</strong></TableCell>
                  <TableCell>
                    {item.type?.name ? <Chip label={item.type.name} size="small" variant="outlined" /> : '-'}
                  </TableCell>
                  <TableCell>{item.make?.name || '-'}</TableCell>
                  <TableCell align="right"><strong>{formatPrice(item.price)}</strong></TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.suppliers || '-'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.description || '-'}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center' }}><strong>No Data</strong></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Per page:"
        showFirstButton
        showLastButton
      />
    </Paper>
  );

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ mb: isMobile ? 1.5 : 2, fontWeight: 600 }}>
        Equipment Catalog
      </Typography>

      {renderFilters()}

      {isMobile ? (
        <>
          {renderMobileCards()}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage=""
            sx={{
              '.MuiTablePagination-toolbar': { pl: 0, pr: 0, minHeight: 44 },
              '.MuiTablePagination-selectLabel': { display: 'none' },
            }}
          />
        </>
      ) : renderDesktopTable()}

      {/* Detail Modal */}
      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        {selectedItem && (
          <>
            <DialogTitle sx={{ fontWeight: 600, fontSize: isMobile ? '1rem' : '1.25rem', pb: 1 }}>
              {selectedItem.name}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 0.5 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {selectedItem.type?.name && <Chip label={selectedItem.type.name} color="primary" variant="outlined" size={isMobile ? 'small' : 'medium'} />}
                  {selectedItem.make?.name && <Chip label={selectedItem.make.name} color="secondary" variant="outlined" size={isMobile ? 'small' : 'medium'} />}
                  {selectedItem.refrigerant && <Chip label={selectedItem.refrigerant} variant="outlined" size={isMobile ? 'small' : 'medium'} />}
                </Stack>

                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#2e7d32' }}>
                  {formatPrice(selectedItem.price)}
                </Typography>

                {selectedItem.suppliers && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Suppliers
                    </Typography>
                    <Typography variant="body2">
                      {selectedItem.suppliers}
                    </Typography>
                  </Box>
                )}

                {selectedItem.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedItem.description}
                  </Typography>
                )}

                {selectedItem.resources?.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedItem.resources.map((r) => (
                      <Box
                        key={r.id}
                        component="img"
                        src={r.url}
                        alt={selectedItem.name}
                        sx={{
                          maxWidth: isMobile ? '100%' : 200,
                          maxHeight: isMobile ? 200 : 150,
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0',
                          bgcolor: '#f5f5f5',
                        }}
                      />
                    ))}
                  </Box>
                )}

                <Divider />

                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Add to Contract
                </Typography>

                <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems={isMobile ? 'stretch' : 'flex-start'}>
                  <TextField
                    size="small"
                    label="Contract Public ID"
                    placeholder="e.g. MA26030601 or NEW"
                    value={publicId}
                    onChange={(e) => { setPublicId(e.target.value.toUpperCase()); setAddResult(null); setShowCreateForm(false); }}
                    sx={{ flexGrow: 1 }}
                    fullWidth={isMobile}
                    helperText="Type NEW to create a new contract"
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddToContract}
                    disabled={addLoading || !publicId.trim()}
                    startIcon={addLoading ? <CircularProgress size={18} color="inherit" /> : <AddCircleOutlineIcon />}
                    sx={{ whiteSpace: 'nowrap', ...(isMobile ? {} : { mt: '0px !important' }) }}
                    fullWidth={isMobile}
                  >
                    Add to Contract
                  </Button>
                </Stack>

                {addResult?.type === 'success' && (
                  <Alert severity="success">
                    Equipment added to <strong>{addResult.publicId}</strong>
                  </Alert>
                )}
                {addResult?.type === 'created' && (
                  <Alert severity="success">
                    Contract <strong>{addResult.publicId}</strong> created and equipment added
                  </Alert>
                )}
                {addResult?.type === 'error' && (
                  <Alert severity="error">{addResult.message}</Alert>
                )}

                {showCreateForm && (
                  <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                      {publicId.trim() === 'NEW' ? 'Create a new HIC (Installation) contract:' : 'Contract not found. Create a new HIC (Installation)?'}
                    </Typography>

                    <Autocomplete
                      options={propertyOptions}
                      getOptionLabel={(opt) => {
                        const addr = opt.address || opt;
                        if (typeof addr === 'string') return addr;
                        return [addr.streetNumber, addr.streetName, addr.city, addr.state, addr.zipCode]
                          .filter(Boolean).join(', ');
                      }}
                      loading={propertyLoading}
                      onInputChange={(e, val) => handlePropertySearch(val)}
                      onChange={(e, val) => setSelectedProperty(val)}
                      value={selectedProperty}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="Search Property"
                          placeholder="Start typing address..."
                        />
                      )}
                      noOptionsText={propertySearch.length < 2 ? 'Type at least 2 characters' : 'No properties found'}
                      sx={{ mb: 2 }}
                    />

                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleCreateContractAndAdd}
                      disabled={createLoading || !selectedProperty}
                      startIcon={createLoading ? <CircularProgress size={18} color="inherit" /> : <AddCircleOutlineIcon />}
                      fullWidth
                    >
                      Create Contract & Add Equipment
                    </Button>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetail}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default EquipmentCatalog;
